import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Participant, SchoolEvent } from 'Src/models/event';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Loader } from 'Src/components/Loader/Loader';
import { TwoPanelEditor } from 'Src/components/TwoPanelEditor/TwoPanelEditor';
import { FILTER_TYPE } from 'Src/consts/table';
import { Switch } from 'Src/components/Switch/Switch';
import { getEventGeneratedName } from 'Src/helpers/accessor/accessor';
import { getUserGenderArrayConvertedFromEventGender } from 'Src/helpers/tournament/tournament';
import { getTournamentEventTeams } from 'Src/helpers/service/adminSU/tournamentTeamsTable';
import { getSchoolUnionTournamentTeamPlayers } from 'Src/helpers/service/adminSU/tournamentTeamPlayersTable';
import { SlotAssignment, SlotAssignmentModal } from 'Src/views/GenericView/AdminView/Tournaments/SlotAssignmentModal';
import {
  getTournamentEventParticipationSummary,
  TournamentEventParticipationSummaryItem,
  updateTournamentEventIndividualsForSchool,
  updateTournamentEventTeamPlayersForSchool
} from 'Src/helpers/service/adminSU/tournamentEventParticipants';
import './EventParticipantsManagerModal.scss';

interface Props {
  user: AppUser;
  event: SchoolEvent;
  participant?: Participant;
  onClose: () => void;
  onSaved: () => void;
}

const userIdOf = (player: any) => player.userId || propz.get(player, ['user', 'id'], '');
const nameOf = (player: any) =>
  `${player.firstName || propz.get(player, ['user', 'firstName'], '')} ${player.lastName ||
    propz.get(player, ['user', 'lastName'], '')}`.trim();

const PARTICIPANT_COLUMNS: any[] = [
  { text: 'Student', field: 'name', isSort: false, type: FILTER_TYPE.NONE, accessor: ['name'] },
  { text: 'Year group', field: 'yearGroup', isSort: false, type: FILTER_TYPE.NONE, accessor: ['yearGroup'] }
];

export const EventParticipantsManagerModal: React.FC<Props> = ({ user, event, participant, onClose, onSaved }) => {
  const [teamMembers, setTeamMembers] = React.useState<any[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [search, setSearch] = React.useState('');
  const [availableSelected, setAvailableSelected] = React.useState<any[]>([]);
  const [eventSelected, setEventSelected] = React.useState<any[]>([]);
  const [isShowYounger, setIsShowYounger] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [participationSummary, setParticipationSummary] = React.useState<TournamentEventParticipationSummaryItem[]>([]);
  const [limitWarnings, setLimitWarnings] = React.useState<string[]>([]);
  const [overrideWarning, setOverrideWarning] = React.useState('');
  const [slotCandidates, setSlotCandidates] = React.useState<any[]>([]);
  const schoolId = propz.get(participant, ['school', 'id'], '');
  const schoolName = propz.get(participant, ['school', 'name'], 'School');
  const eventTeamId = propz.get(participant, ['team', 'id'], '');
  const maxPlayers =
    propz.get<number>(event, ['sport', 'defaultLimits', 'maxPlayers']) ||
    propz.get<number>(event, ['tournament', 'maxPlayersPerTeam']);
  const isLimitEnforced = propz.get<any>(event, ['tournament', 'isEventParticipantLimitEnforced'], true) !== false;
  const hasPlayerLimit = isLimitEnforced && typeof maxPlayers === 'number' && maxPlayers > 0;
  const isPlayerLimitExceeded = hasPlayerLimit && selectedIds.length > maxPlayers;
  const canOverridePlayerLimit =
    user.activeRole === 'ADMIN' &&
    [propz.get(event, ['tournament', 'schoolId'], ''), event.eventCreatorId, event.inviterSchoolId].some(
      id => id && String(user.activeSchoolId) === String(id)
    );
  const playerLimitExcess = isPlayerLimitExceeded ? selectedIds.length - maxPlayers : 0;
  const currentPlayers: any[] = eventTeamId
    ? propz.get(participant, ['teamPlayers'], [])
    : propz.get(participant, ['individualPlayers'], []);

  React.useEffect(() => {
    if (!participant || !schoolId) return;
    const tournamentId = event.tournamentId || propz.get(event, ['tournament', 'id'], '');
    const embeddedSourceId =
      propz.get(participant, ['team', 'cloneOf'], '') || propz.get(participant, ['team', 'tournamentTeamId'], '');
    setIsLoading(true);
    setError('');
    setSearch('');
    setAvailableSelected([]);
    setEventSelected([]);
    setIsShowYounger(false);
    setLimitWarnings([]);
    setParticipationSummary([]);
    setSelectedIds(currentPlayers.map(userIdOf));
    const sourceIdPromise = embeddedSourceId
      ? Promise.resolve(embeddedSourceId)
      : getTournamentEventTeams(user, tournamentId, { where: { schoolId, isCreator: true }, limit: 100, skip: 0 }).then(
          (teams: any[]) => {
            const team = (teams || []).find(item => item.schoolId === schoolId) || (teams || [])[0];
            return propz.get(team, ['id'], propz.get(team, ['_id'], ''));
          }
        );
    sourceIdPromise
      .then(teamId => {
        if (!teamId) throw new Error('Tournament team not found');
        return getSchoolUnionTournamentTeamPlayers(
          user,
          user.activeSchoolId,
          tournamentId,
          teamId,
          { limit: 1000, skip: 0 },
          schoolId,
          { includeEvents: false }
        );
      })
      .then(items => {
        const members = items || [];
        setTeamMembers(members);
        const userIds = members.map(userIdOf).filter(Boolean);
        return userIds.length && tournamentId
          ? getTournamentEventParticipationSummary(user, tournamentId, userIds)
          : [];
      })
      .then(items => setParticipationSummary(items || []))
      .catch(() => setError('The tournament team members could not be loaded. Please try again.'))
      .finally(() => setIsLoading(false));
  }, [participant, schoolId]);

  const allMembers = [...teamMembers];
  currentPlayers.forEach(player => {
    if (!allMembers.some(member => userIdOf(member) === userIdOf(player))) allMembers.push(player);
  });
  const query = search.trim().toLowerCase();
  const eventAges = event.ages || [];
  const allowedGenders = getUserGenderArrayConvertedFromEventGender(event.gender);
  const isEligible = (player: any) => {
    const gender = player.gender || propz.get(player, ['user', 'gender']);
    const age = Number(propz.get(player, ['form', 'age']));
    const isGenderEligible = !allowedGenders || allowedGenders.includes(gender);
    const isAgeEligible =
      eventAges.length === 0 ||
      (Number.isFinite(age) && (isShowYounger ? eventAges.some(eventAge => eventAge >= age) : eventAges.includes(age)));
    return isGenderEligible && isAgeEligible;
  };
  const visibleAvailable = teamMembers.filter(
    player =>
      isEligible(player) &&
      (!query ||
        nameOf(player)
          .toLowerCase()
          .includes(query))
  );
  const toEditorItem = (player: any) => ({
    ...player,
    id: userIdOf(player),
    name: nameOf(player) || 'Student',
    yearGroup: propz.get(player, ['form', 'name'], propz.get(player, ['form', 'ageGroup'], 'Not set'))
  });
  const available = visibleAvailable.filter(player => !selectedIds.includes(userIdOf(player))).map(toEditorItem);
  const selected = allMembers.filter(player => selectedIds.includes(userIdOf(player))).map(toEditorItem);
  const noOp = () => undefined;
  const participationLimit = Number(
    propz.get(event, ['tournament', eventTeamId ? 'maxRelayEventsPerStudent' : 'maxIndividualEventsPerStudent'], 0)
  );
  const getParticipationLimitWarnings = (ids: string[]) =>
    ids.reduce((warnings: string[], userId) => {
      if (participationLimit <= 0 || currentPlayers.some(player => userIdOf(player) === userId)) return warnings;
      const otherEvents = participationSummary.filter(
        item => item.userId === userId && item.eventId !== event.id && item.isRelay === Boolean(eventTeamId)
      );
      if (otherEvents.length < participationLimit) return warnings;
      const player = allMembers.find(item => userIdOf(item) === userId);
      const eventNames = otherEvents.map(item => {
        const date = item.startTime ? new Date(item.startTime).toLocaleDateString('en-GB') : '';
        return (
          item.shortDescription || [item.sportName, date].filter(Boolean).join(' · ') || 'another tournament event'
        );
      });
      warnings.push(
        `${nameOf(player) || 'Student'} is already entered in ${eventNames.join(
          ', '
        )} and has reached the limit of ${participationLimit} ${eventTeamId ? 'relay' : 'individual'} ${
          participationLimit === 1 ? 'event' : 'events'
        }.`
      );
      return warnings;
    }, []);
  const togglePicked = (current: any[], item: any, setter: React.Dispatch<React.SetStateAction<any[]>>) =>
    setter(
      current.some(value => value.id === item.id) ? current.filter(value => value.id !== item.id) : [...current, item]
    );
  const selectedPlayers = allMembers.filter(player => selectedIds.includes(userIdOf(player)));
  const requestAdd = (candidateIds: string[]) => {
    const uniqueCandidateIds = candidateIds.filter(id => !selectedIds.includes(id));
    if (hasPlayerLimit && selectedIds.length + uniqueCandidateIds.length > maxPlayers) {
      setSlotCandidates(teamMembers.filter(player => uniqueCandidateIds.includes(userIdOf(player))));
      setAvailableSelected([]);
      return;
    }
    const warnings = getParticipationLimitWarnings(uniqueCandidateIds);
    setLimitWarnings(warnings);
    if (!warnings.length || canOverridePlayerLimit) {
      setSelectedIds(ids => Array.from(new Set([...ids, ...uniqueCandidateIds])));
    }
    setAvailableSelected([]);
  };
  const addSelected = () => {
    setError('');
    requestAdd(availableSelected.map(item => item.id));
  };

  const confirmSlotAssignments = (assignments: SlotAssignment[]) => {
    const assignedIds = assignments.map(item => item.student.userId);
    const warnings = getParticipationLimitWarnings(assignedIds);
    setLimitWarnings(warnings);
    if (warnings.length && !canOverridePlayerLimit) return;

    const replacedUserIds = assignments.reduce((ids, assignment) => {
      if (!assignment.replacingPlayerId) return ids;
      const replaced = selectedPlayers.find(
        player => String(player.id || player._id || userIdOf(player)) === String(assignment.replacingPlayerId)
      );
      if (replaced) ids.push(userIdOf(replaced));
      return ids;
    }, [] as string[]);
    setSelectedIds(ids => Array.from(new Set([...ids.filter(id => !replacedUserIds.includes(id)), ...assignedIds])));
    setSlotCandidates([]);
  };
  const removeSelected = () => {
    setError('');
    setSelectedIds(ids => ids.filter(id => !eventSelected.some(item => item.id === id)));
    setEventSelected([]);
  };

  const submitChanges = (override = false) => {
    const currentIds = currentPlayers.map(userIdOf);
    const add = teamMembers
      .filter(player => selectedIds.includes(userIdOf(player)) && !currentIds.includes(userIdOf(player)))
      .map(player => ({ userId: userIdOf(player), permissionId: player.permissionId }));
    const remove = currentPlayers
      .filter(player => !selectedIds.includes(userIdOf(player)))
      .map(player => player.id)
      .filter(Boolean);
    setIsSaving(true);
    setError('');
    const submit = () =>
      eventTeamId
        ? updateTournamentEventTeamPlayersForSchool(
            user,
            event.id,
            schoolId,
            eventTeamId,
            { add, remove, update: [] },
            override
          )
        : updateTournamentEventIndividualsForSchool(user, event.id, schoolId, { add, remove, update: [] }, override);
    const request = submit().catch(error => {
      const text = propz.get(error, ['response', 'data', 'details', 'text'], '');
      const canOverrideTournamentLimit = text.indexOf('organising union may override') !== -1;
      if (!override && canOverrideTournamentLimit && canOverridePlayerLimit) {
        setOverrideWarning(text);
        return undefined;
      }
      throw error;
    });
    request
      .then(result => result && onSaved())
      .catch(error =>
        setError(
          propz.get(error, ['response', 'data', 'details', 'text'], 'Changes could not be saved. Please try again.')
        )
      )
      .finally(() => setIsSaving(false));
  };

  const save = () => {
    const currentIds = currentPlayers.map(userIdOf);
    const addedIds = selectedIds.filter(id => !currentIds.includes(id));
    const participationWarnings = getParticipationLimitWarnings(addedIds);
    const warnings = [...participationWarnings];
    if (isPlayerLimitExceeded) {
      warnings.unshift(
        `This roster contains ${selectedIds.length} students, exceeding the maximum of ${maxPlayers} by ${playerLimitExcess}.`
      );
    }
    setLimitWarnings(warnings);
    if (warnings.length) {
      if (canOverridePlayerLimit) {
        setOverrideWarning(warnings.join(' '));
      } else {
        setError('Remove the over-limit students before saving this event roster.');
      }
      return;
    }
    submitChanges(false);
  };

  return (
    <SimpleModal
      customClass="eventParticipantsManagerModal"
      isOpen={Boolean(participant)}
      title={
        <div className="eventParticipantsManagerModal__title">
          <strong>{propz.get(event, ['tournament', 'name'], 'Tournament')}</strong>
          <span>
            {getEventGeneratedName(event, { user }) || 'Event'} · {schoolName}
          </span>
        </div>
      }
      onCloseClick={onClose}
      onBackdropClick={onClose}
      showFooter={false}
      isBodyBold={false}
    >
      {slotCandidates.length > 0 && (
        <SlotAssignmentModal
          isOpen
          event={event}
          newStudents={slotCandidates as any}
          currentParticipants={selectedPlayers}
          maxPlayers={maxPlayers}
          activeSchoolId={user.activeSchoolId}
          onConfirm={confirmSlotAssignments}
          onClose={() => setSlotCandidates([])}
        />
      )}
      <div className="eventParticipantsManagerModal__toolbar">
        <div>
          <strong>Build the event roster</strong>
          <span>
            {isPlayerLimitExceeded
              ? canOverridePlayerLimit
                ? `Warning: this exceeds the sport limit by ${playerLimitExcess}. As the organising union administrator, you may save anyway.`
                : `Return ${playerLimitExcess} ${
                    playerLimitExcess === 1 ? 'student' : 'students'
                  } to Team members before saving.`
              : 'Select event participants from the tournament team.'}
          </span>
        </div>
        <div className="eventParticipantsManagerModal__summary">
          <span className={isPlayerLimitExceeded ? 'is-over-limit' : undefined}>
            <strong>{selectedIds.length}</strong> selected
          </span>
          {hasPlayerLimit && (
            <>
              <span className={isPlayerLimitExceeded ? 'is-over-limit' : undefined}>
                <strong>{maxPlayers}</strong> maximum
              </span>
              <span className={isPlayerLimitExceeded ? 'is-over-limit' : 'is-available'}>
                <strong>
                  {isPlayerLimitExceeded ? playerLimitExcess : Math.max(0, maxPlayers - selectedIds.length)}
                </strong>{' '}
                {isPlayerLimitExceeded ? 'over limit' : 'places available'}
              </span>
            </>
          )}
        </div>
      </div>
      {error && <div className="eventParticipantsManagerModal__error">{error}</div>}
      {limitWarnings.length ? (
        <div className="eventParticipantsManagerModal__limitWarnings" role="alert">
          <span aria-hidden="true">!</span>
          <div>
            <strong>Participation limit reached</strong>
            {limitWarnings.map(message => (
              <p key={message}>{message}</p>
            ))}
            {canOverridePlayerLimit ? (
              <small>The organising union administrator can save an exceptional entry.</small>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="eventParticipantsManagerModal__editor">
        {isLoading ? (
          <Loader />
        ) : (
          <TwoPanelEditor
            isSourceFirst
            items={available}
            itemsTitle="Team members"
            itemsHeaderExtra={
              <Switch
                name="showYoungerEventParticipants"
                checked={isShowYounger}
                onChange={() => {
                  setIsShowYounger(value => !value);
                  setAvailableSelected([]);
                }}
                text="Show younger students"
              />
            }
            itemsColumns={PARTICIPANT_COLUMNS}
            itemsSelected={availableSelected}
            onItemClick={index => togglePicked(availableSelected, available[index], setAvailableSelected)}
            itemsFilters={{}}
            onItemsFilterChange={noOp}
            isShowItemsFilter={false}
            isItemsFiltered={Boolean(search)}
            onItemsFilterClick={noOp}
            itemsSetCurrentPageParams={noOp}
            itemsAccumulator={selected}
            itemsAccumulatorTitle="This event"
            itemsAccumulatorSearchByNameOnly
            itemsAccumulatorColumns={PARTICIPANT_COLUMNS}
            itemsAccumulatorSelected={eventSelected}
            onItemAccumulatorClick={index => togglePicked(eventSelected, selected[index], setEventSelected)}
            itemsAccumulatorFilters={{}}
            onItemsAccumulatorFilterChange={noOp}
            isShowItemsAccumulatorFilter={false}
            isItemsAccumulatorFiltered={false}
            onItemsAccumulatorFilterClick={noOp}
            onAddClick={addSelected}
            onRemoveClick={removeSelected}
            itemsCount={available.length}
            itemsAccumulatorCount={selected.length}
            itemsAccumulatorCapacity={hasPlayerLimit ? maxPlayers : undefined}
            itemCurrentPage={1}
            itemAccumulatorCurrentPage={1}
            itemsOnApplyFilterClick={noOp}
            itemsOnClearFilterClick={() => setSearch('')}
            itemsAccumulatorOnApplyFilterClick={noOp}
            itemsAccumulatorOnClearFilterClick={noOp}
            onSelectAllItemsClick={() =>
              setAvailableSelected(current => {
                const selectedIds = new Set(current.map(userIdOf));
                return [...current, ...available.filter(item => !selectedIds.has(userIdOf(item)))];
              })
            }
            onSelectAllItemsOnPageClick={() =>
              setAvailableSelected(current => {
                const selectedIds = new Set(current.map(userIdOf));
                return [...current, ...available.filter(item => !selectedIds.has(userIdOf(item)))];
              })
            }
            onUnselectAllItemsClick={() => setAvailableSelected([])}
            isSelectAllItemsChecked={available.length > 0 && availableSelected.length === available.length}
            onSelectAllItemsAccumulatorClick={() =>
              setEventSelected(current => {
                const selectedIds = new Set(current.map(userIdOf));
                return [...current, ...selected.filter(item => !selectedIds.has(userIdOf(item)))];
              })
            }
            onSelectAllItemsAccumulatorOnPageClick={() =>
              setEventSelected(current => {
                const selectedIds = new Set(current.map(userIdOf));
                return [...current, ...selected.filter(item => !selectedIds.has(userIdOf(item)))];
              })
            }
            onUnselectAllItemsAccumulatorClick={() => setEventSelected([])}
            isSelectAllItemsAccumulatorChecked={selected.length > 0 && eventSelected.length === selected.length}
            sortItemsDirection={'' as any}
            sortItemsColumnsName=""
            onSortItemsClick={noOp}
            sortItemsAccumulatorDirection={'' as any}
            sortItemsAccumulatorColumnsName=""
            onSortItemsAccumulatorClick={noOp}
            itemsAddBuffer={selected.filter(item => !currentPlayers.some(player => userIdOf(player) === item.id))}
            itemsRemoveBuffer={currentPlayers.filter(player => !selectedIds.includes(userIdOf(player)))}
            onAddSingleItem={item => {
              setError('');
              requestAdd([item.id]);
            }}
            onRemoveSingleItem={item => {
              setError('');
              setSelectedIds(ids => ids.filter(id => id !== item.id));
              setEventSelected([]);
            }}
            unitLabel="students"
            showFilterControl={false}
            showAvatars
            onItemsSearchChange={setSearch}
            itemsSearchValue={search}
            itemsModernPagination
          />
        )}
      </div>
      <div className="eventParticipantsManagerModal__actions">
        <button type="button" className="sit-btn sit-btn--secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </button>
        <button
          type="button"
          className="sit-btn sit-btn--primary"
          onClick={save}
          disabled={isLoading || isSaving || Boolean(error) || (isPlayerLimitExceeded && !canOverridePlayerLimit)}
          title={isPlayerLimitExceeded ? `Remove ${playerLimitExcess} over-limit participant before saving` : undefined}
        >
          {isSaving ? 'Saving…' : 'Save participants'}
        </button>
      </div>
      <SimpleModal
        isOpen={Boolean(overrideWarning)}
        title="Override participation limits?"
        showFooter={false}
        customClass="eventParticipantsManagerOverrideModal"
        dialogStyle={{ maxWidth: 500 }}
        onCloseClick={() => setOverrideWarning('')}
      >
        <section className="eventParticipantsManagerModal__overrideWarning" role="alertdialog" aria-modal="true">
          <span aria-hidden="true">!</span>
          <div>
            <strong>This creates an exceptional tournament entry</strong>
            <p>{overrideWarning}</p>
            <small>The configured limits will remain unchanged for all other entries.</small>
          </div>
        </section>
        <div className="eventParticipantsManagerModal__actions">
          <button type="button" className="sit-btn sit-btn--secondary" onClick={() => setOverrideWarning('')}>
            Cancel
          </button>
          <button
            type="button"
            className="sit-btn sit-btn--danger"
            disabled={isSaving}
            onClick={() => {
              setOverrideWarning('');
              submitChanges(true);
            }}
          >
            Override and save
          </button>
        </div>
      </SimpleModal>
    </SimpleModal>
  );
};
